import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { Box, Typography } from '@mui/material';

import { getCaseLedger } from '../../api/paymentsApi/settlementsApi';
import Loader from '../../components/Loader';
import NoDataText from '../../components/NoDataText';
import TableContainer from '../../components/Table/TableContainer';
import { CASE_LEDGER_LIST_TABLE_LABELS } from '../../constants/tableLabels';
import {
    ActiveSortField,
    FilterTypes,
    LedgerEntry,
    LedgerSettlementOption,
    LedgerTiles,
    SelectOption,
} from '../../types';
import useSnackBar from '../../hooks/useSnackBar';
import TableActions from '../../components/Table/TableActions';
import TableFilters from '../../components/TableFilters/TableFilters';
import { ALL_SETTLEMENTS_FILTER_VALUE } from '../../components/TableFilters/constants';
import { downloadCsvorExcel } from '../../helpers/downloadCsv';
import usePagination from '../../hooks/usePagination';
import FormSearchField from '../../components/formFields/FormSearchField';
import { validateTextFieldValue } from '../../helpers/validateTextFieldValue';
import CaseLedgerTableDataRow, { getLedgerCategoryLabel } from './CaseLedgerTableDataRow';
import CaseLedgerSummaryCards from './CaseLedgerSummaryCards';
import theme from '../../theme';

const EMPTY_LEDGER_TILES: LedgerTiles = {
    posted: { balance: 0, credits: 0, debits: 0 },
    inTransit: { balance: 0, credits: 0, debits: 0 },
    projected: { balance: 0, credits: 0, debits: 0, pendingLegs: 0 },
};

const CaseLedgerView = () => {
    const { caseId } = useParams();
    const { setAlert } = useSnackBar();
    const { pageNumber, rowsPerPage, setTotalCount, setPageNumber } = usePagination();

    const [ledgerEntries, setLedgerEntries] = useState<LedgerEntry[]>([]);
    const [ledgerTiles, setLedgerTiles] = useState<LedgerTiles>(EMPTY_LEDGER_TILES);
    const [settlementOptions, setSettlementOptions] = useState<LedgerSettlementOption[]>([]);
    const [isDataLoading, setIsDataLoading] = useState(true);
    const [activeSortField, setActiveSortField] = useState<ActiveSortField | null>(null);
    const [showFilters, setShowFilters] = useState<boolean>(true);
    const [filters, setFilters] = useState<FilterTypes>({
        settlementId: ALL_SETTLEMENTS_FILTER_VALUE,
    });
    const [searchValue, setSearchValue] = useState<string>('');

    const searchFormMethods = useForm({ defaultValues: { searchQuery: '' } });

    // "All Settlements" goes out as `view=ALL`; a specific settlement as `view=SETTLEMENT&settlementId=<id>`
    // Only these params are sent: the ledger API rejects filter[...] params (e.g. firmId set by "Clear filters")
    const getLedgerQueryFilters = (): FilterTypes => {
        const { settlementId } = filters;
        const selectedOption =
            settlementId && settlementId !== ALL_SETTLEMENTS_FILTER_VALUE
                ? settlementOptions.find((option) => String(option.settlementId) === settlementId)
                : undefined;

        return {
            ...(selectedOption
                ? { view: 'SETTLEMENT', settlementId: String(selectedOption.settlementId) }
                : { view: 'ALL' }),
        };
    };

    useEffect(() => {
        if (!caseId) return;

        setIsDataLoading(true);
        getCaseLedger(caseId, getLedgerQueryFilters())
            .then((res) => {
                if (res.data) {
                    const entries = res.data.entries || [];
                    setLedgerEntries(entries);
                    setLedgerTiles(res.data.tiles || EMPTY_LEDGER_TILES);
                    setSettlementOptions(res.data.settlementOptions || []);
                }
            })
            .catch((error) => {
                setAlert({
                    message: error.response?.data?.message || 'Failed to load ledger',
                    type: 'error',
                });
            })
            .finally(() => {
                setIsDataLoading(false);
            });
    }, [caseId, filters]);

    const settlementFilterOptions: SelectOption[] = useMemo(
        () => [
            { id: ALL_SETTLEMENTS_FILTER_VALUE, label: ALL_SETTLEMENTS_FILTER_VALUE },
            ...settlementOptions.map((option) => ({
                id: String(option.settlementId),
                label: option.label,
            })),
        ],
        [settlementOptions],
    );

    // Search runs in the UI only, over the entries already loaded for the selected settlement
    const filteredEntries = useMemo(() => {
        const query = searchValue.trim().toLowerCase();
        if (!query) return ledgerEntries;

        return ledgerEntries.filter((entry) =>
            [entry.description, getLedgerCategoryLabel(entry.category)].some((field) => field?.toLowerCase().includes(query)),
        );
    }, [ledgerEntries, searchValue]);

    useEffect(() => {
        setTotalCount?.(filteredEntries.length);
    }, [filteredEntries]);

    // The ledger API returns every entry at once, so pagination is applied client-side
    const paginatedEntries = useMemo(
        () => filteredEntries.slice((pageNumber - 1) * rowsPerPage, pageNumber * rowsPerPage),
        [filteredEntries, pageNumber, rowsPerPage],
    );

    const handleSearchChange = (value: string) => {
        const cleanedValue = validateTextFieldValue(value);
        searchFormMethods.setValue('searchQuery', cleanedValue);
        setSearchValue(cleanedValue);
        setPageNumber?.(1);
    };

    const handleResetSearch = () => {
        searchFormMethods.reset();
        setSearchValue('');
        setPageNumber?.(1);
    };

    const exportList = () => {
        downloadCsvorExcel(`/v1/fo/cases/${caseId}/ledger/export`, 'GET', 'Case_Ledger', 'csv', {
            page: pageNumber,
            size: rowsPerPage,
            filters: getLedgerQueryFilters(),
        }).then((res) => {
            if (res?.data) {
                setAlert({
                    message: 'Ledger exported successfully.',
                    type: 'success',
                });
            }
        });
    };

    return (
        <>
            <TableActions
                hasParams={true}
                exportList={exportList}
                showFilters={showFilters}
                setShowFilters={setShowFilters}
            />

            {showFilters && (
                <TableFilters
                    filters={filters}
                    setFilters={setFilters}
                    ledgerSettlementFilter={settlementFilterOptions}
                />
            )}

            <CaseLedgerSummaryCards tiles={ledgerTiles} />

            <Box mb="16px">
                <FormProvider {...searchFormMethods}>
                    <form style={{ width: '100%' }} onSubmit={(event) => event.preventDefault()}>
                        <FormSearchField
                            name="searchQuery"
                            searchValue={searchValue}
                            handleChange={handleSearchChange}
                            handleResetSearch={handleResetSearch}
                        />
                    </form>
                </FormProvider>
            </Box>

            {isDataLoading && !ledgerEntries.length ? (
                <Box position="absolute" top="50%" left="50%">
                    <Loader colorType="warning" />
                </Box>
            ) : !filteredEntries.length ? (
                <NoDataText marginTop='100px'/>
            ) : (
                <>
                    <TableContainer
                        headerLabels={CASE_LEDGER_LIST_TABLE_LABELS}
                        activeSortField={activeSortField}
                        setActiveSortField={setActiveSortField}
                    >
                        <CaseLedgerTableDataRow tableData={paginatedEntries} />
                    </TableContainer>

                    <Typography mt={2} sx={{ color: theme.palette.text.secondary, fontFamily: 'Inter', fontSize: '14px' }}>
                        Every credit and debit links back to the settlement that produced it. Lead attorney fees, co-counsel fees and referring fees are listed separately.
                    </Typography>
                </>
            )}


        </>
    );
};

export default CaseLedgerView;
